import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, History, Inbox, Scale, LogOut, ShieldCheck } from 'lucide-react';
import './styles/Header.css';
import ThemeToggle from './styles/ThemeToggle';
import UserAvatar from './UserAvatar';
import { CLOVER_ALLOWED_USERS } from '../utils/userUtils';
import { API_URL } from '../config/api';

// Main sections, shown as text
const SECTIONS = [
  { to: '/', label: 'WOE', title: 'Work Order Entry' },
  { to: '/orderdb', label: 'OrderDB' },
  { to: '/customers', label: 'Customers' },
  { to: '/inventory', label: 'Inventory' },
  { to: '/rachel-room', label: 'Rachel Room', shortLabel: 'RR' },
  { to: '/appointment', label: 'Appointment' }
];

// A section stays highlighted on its sub-pages too (e.g. /customers/123)
const isCurrent = (pathname, to) =>
  to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

const Header = ({ user, onLogout, onUserUpdate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAdminUser, setIsAdminUser] = useState(false);

  const canSeeRepairBalances = CLOVER_ALLOWED_USERS.includes(
    (user?.UserName || '').toLowerCase()
  );

  useEffect(() => {
    if (!user) return;
    axios.get(`${API_URL}/api/admin/check`, {
      headers: { 'x-username': user.UserName }
    })
      .then(() => setIsAdminUser(true))
      .catch(() => setIsAdminUser(false));
  }, [user]);

  // Shortcuts, shown as icons
  const shortcuts = [
    { to: '/reminders', label: 'Reminders', Icon: Bell },
    { to: '/rescheduled-appointments', label: 'Rescheduled appointments', Icon: History },
    { to: '/leads', label: 'Website leads', Icon: Inbox },
    canSeeRepairBalances && { to: '/reports/wig-repair-balances', label: 'Wig repair balances', Icon: Scale }
  ].filter(Boolean);

  const fullName = user ? (user.UserFullName || `${user.UserFirstName} ${user.UserLastName}`) : '';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <header className="app-header">
      <div className="header-container">
        <Link to="/" className="header-logo" title="Work Order Entry">
          <img src="/images/dini_title.png" alt="DiNi Wigs" />
        </Link>

        <nav className="header-nav" aria-label="Main">
          <ul className="header-sections">
            {SECTIONS.map(({ to, label, shortLabel, title }) => {
              const current = isCurrent(location.pathname, to);
              return (
                <li key={to}>
                  <Link
                    to={to}
                    className={`header-link${current ? ' is-current' : ''}`}
                    aria-current={current ? 'page' : undefined}
                    title={title}
                  >
                    {shortLabel ? (
                      <>
                        <span className="header-label-full">{label}</span>
                        <span className="header-label-short" aria-hidden="true">{shortLabel}</span>
                      </>
                    ) : label}
                  </Link>
                </li>
              );
            })}
          </ul>

          <ul className="header-shortcuts">
            {shortcuts.map(({ to, label, Icon }) => {
              const current = isCurrent(location.pathname, to);
              return (
                <li key={to}>
                  <Link
                    to={to}
                    className={`header-icon-link${current ? ' is-current' : ''}`}
                    aria-current={current ? 'page' : undefined}
                    aria-label={label}
                    title={label}
                  >
                    <Icon aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="header-account">
          <ThemeToggle />

          {user && (
            <>
              {isAdminUser ? (
                <button
                  type="button"
                  className="header-user header-user--admin"
                  onClick={() => navigate('/system-admin')}
                  title={`${greeting}, ${fullName}. Open System Admin`}
                >
                  <span className="header-user-name">{fullName}</span>
                  <ShieldCheck aria-hidden="true" />
                </button>
              ) : (
                <span className="header-user" title={`${greeting}, ${fullName}`}>
                  <span className="header-user-name">{fullName}</span>
                </span>
              )}

              <UserAvatar user={user} onUserUpdate={onUserUpdate} />

              <button type="button" className="header-logout" onClick={onLogout} title="Log out">
                <LogOut aria-hidden="true" />
                <span className="header-logout-text">Log out</span>
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
